import { RefAttributes, forwardRef, Ref, type JSX, useCallback, ChangeEvent, FocusEvent, ReactElement } from "react";

import Grid from "@mui/material/Grid";
import { FieldValues, useWatch, useController } from "react-hook-form";

import { isInputRequired, getLabel } from "../../../helpers/validators";
import { useCurrentInputVibe } from "../../../hooks/useCurrentInputVibe";
import { useCustomError } from "../../../hooks/useCustomError";
import { useValidators } from "../../../hooks/useValidators";
import { useDebounce } from "../../TextField/useDebounce";
import { DashboardBaseInput } from "../BaseInput/DashboardBaseInput";
import { DashboardInputLabel } from "../InputLabel/DashboardInputLabel";

import { FormTextFieldProps, TextFieldProps, BaseTextFieldProps, truncateTypes } from "./types";

import { useFormInputRefs } from "@/hooks/useFormInputRefs";

function DashboardTextFieldInner<T extends FieldValues>(
  { control, rules, name, shouldUnregister, ...rest }: TextFieldProps<T>,
  ref: Ref<HTMLInputElement>
) {
  return control && name ? (
    <FormTextField
      {...rest}
      ref={ref}
      control={control}
      rules={rules}
      name={name}
      shouldUnregister={shouldUnregister}
    />
  ) : (
    <BaseTextField {...rest} name={name} ref={ref} />
  );
}

DashboardTextFieldInner.displayName = "TextField";

export const DashboardTextField = forwardRef(DashboardTextFieldInner) as <T extends FieldValues>(
  props: TextFieldProps<T> & RefAttributes<HTMLInputElement>
) => JSX.Element;

const FormTextField = forwardRef(FormTextFieldInner) as unknown as <T extends FieldValues>(
  props: FormTextFieldProps<T> & RefAttributes<HTMLInputElement>
) => JSX.Element;

function FormTextFieldInner<T extends FieldValues>(
  {
    control,
    debounced,
    defaultValue,
    name,
    onBlur,
    onChange,
    rules,
    shouldUnregister,
    inputRef,
    noAsterisk,
    ...rest
  }: FormTextFieldProps<T>,
  ref: Ref<HTMLInputElement>
) {
  const validators = useValidators(rules);
  const isRequired = isInputRequired(validators);

  const watchedValue = useWatch({ control, name });
  const internalDefaultValue = defaultValue || watchedValue;

  const { field } = useController<T>({
    name,
    control,
    defaultValue: internalDefaultValue as never,
    rules: validators,
    shouldUnregister,
  });

  const refCallBack = useFormInputRefs({ inputRefCallBack: field.ref, inputRef });

  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      if (event) {
        onChange?.(event);
        field.onChange(event.target.value);
      }
    },
    [field, onChange]
  );

  const handleBlur = useCallback(
    (event: FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      onBlur?.(event);
      field.onBlur();
    },
    [field, onBlur]
  );

  return (
    <BaseTextField
      {...rest}
      // Cannot have a controlled debounced TextField for now
      debounced={debounced}
      // Unfortunately, useController does not allow us to defined the type of field.value
      // type-coverage:ignore-next-line
      value={debounced ? undefined : field.value}
      inputRef={refCallBack}
      ref={ref}
      defaultValue={debounced ? internalDefaultValue : undefined}
      onBlur={handleBlur}
      onChange={handleChange}
      data-test={rest?.["data-test"] || "text-field"}
      label={getLabel(isRequired, rest.label, noAsterisk)}
      name={name}
    />
  );
}

const BaseTextField = forwardRef<HTMLInputElement, BaseTextFieldProps>(
  (
    {
      classes,
      className,
      vibe,
      debounced,
      defaultValue,
      error,
      errorMessage,
      helperText,
      headerRight,
      InputProps,
      label,
      endElement,
      onBlur,
      onChange,
      onChangeNotDebounced,
      tooltipText,
      type,
      "data-test": dataTest,
      truncate = true,
      ...rest
    },
    ref
  ): ReactElement<BaseTextFieldProps> => {
    const currentInputVibe = useCurrentInputVibe(vibe);

    const currentError = useCustomError(error, errorMessage);

    const handleChange = useCallback(
      (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        if (event.target.type === "number") {
          const value = event.target.value.length > 0 ? parseFloat(event.target.value) : null;
          onChange?.({
            ...event,
            target: {
              ...event.target,
              value: value as never,
            },
          });
        } else {
          onChange?.(event);
        }
      },
      [onChange]
    );

    const { setDebouncedValue } = useDebounce({
      debounced,
      onChange,
    });

    const handleChangeDebounced = useCallback(
      (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        onChangeNotDebounced?.(event);
        setDebouncedValue(event);
      },
      [onChangeNotDebounced, setDebouncedValue]
    );

    const handleBlur = useCallback(
      (event: FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        if (
          truncate &&
          truncateTypes.includes(event.target.type) &&
          event.target.value !== event.target.value?.trim()
        ) {
          const truncatedEvent = { ...event, target: { ...event.target, value: event.target.value?.trim() } };
          if (debounced) {
            handleChangeDebounced?.(truncatedEvent);
          } else {
            handleChange?.(truncatedEvent);
          }
          onBlur?.(truncatedEvent);
        } else {
          onBlur?.(event);
        }
      },
      [debounced, handleChange, handleChangeDebounced, onBlur, truncate]
    );

    const onWheel = useCallback(
      (event: React.WheelEvent<HTMLDivElement>) =>
        type === "number" ? event.currentTarget.querySelector("input")?.blur() : null,
      [type]
    );

    const { inputLabel: inputLabelClasses, ...classesRest } = classes || {};

    return (
      <DashboardInputLabel
        classes={inputLabelClasses}
        className={className}
        vibe={currentInputVibe}
        data-test={dataTest}
        errorMessage={errorMessage}
        helperText={helperText}
        headerRight={headerRight}
        label={label}
        tooltipText={tooltipText}
      >
        <Grid container spacing={1} alignItems="center">
          <Grid item xs>
            <DashboardBaseInput
              {...rest}
              ref={ref}
              classes={classesRest}
              className={InputProps?.className}
              vibe={currentInputVibe}
              error={currentError}
              defaultValue={defaultValue}
              onBlur={handleBlur}
              onChange={debounced ? handleChangeDebounced : handleChange}
              type={type}
              onWheel={onWheel}
            />
          </Grid>
          {endElement && <Grid item>{endElement}</Grid>}
        </Grid>
      </DashboardInputLabel>
    );
  }
);

BaseTextField.displayName = "BaseTextField";
